import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { FieldType } from "@ngx-formly/core";
import { Subscription } from "rxjs";
import { AppStateService } from "src/app/core-module/services/app-state.service";
import { AloAocStateService } from "../../../core-module/services/state/alo-aoc-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { MiscFeatureStateService } from "../../../core-module/services/state/misc-feature-state.service";


const customMinMaxValidator = (propsDetails, prop) => {
  return (control: FormControl) => {
    const propData = propsDetails[prop];
    const value = control.value;
    const min = propData.min;
    const max = propData.max;
    if (value < min || value > max) return { error: true };
    return null;
  };
};

const customCCTValidator = (
  formgroup: FormGroup,
  checkprop,
  condition,
  min,
  max,
  checkMin,
  checkMax
) => {
  return (control: FormControl) => {
    const checkControl = formgroup.get(checkprop);
    const controlValue = control.value;
    const checkValue = checkControl.value;
    let controlError = null;
    if (
      controlValue > max ||
      controlValue < min ||
      checkValue < checkMin ||
      checkValue > checkMax ||
      (condition == "lessthanequal" && controlValue > checkValue) ||
      (condition == "greaterthanequal" && controlValue < checkValue)
    ) {
      controlError = { error: true };
    }
    return controlError;
  };
};

@Component({
  standalone: false,
  selector: "app-flextune",
  templateUrl: "./flextune.component.html",
  styleUrls: ["./flextune.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlextuneComponent extends FieldType implements OnInit, OnDestroy {
  @Input() field;

  valueChangesSubs: { [key: string]: Subscription } = {};
  properties: LegacyAny;
  private readonly quickConfigvalueSignal = signal(true); // Default to Standard mode (sliders hidden)
  allDropDownItems = {};
  propertiesInfo = {};

  private readonly isIndoorSignal = signal(false);
  private readonly isFromDeviceSignal = signal(false);
  applicableProperties = [];
  flextuneFormGroup: FormGroup = new FormGroup({});
  private quickConfigSub: Subscription;
  private formValueChangesSub: Subscription;
  private restoreDefaultSub: Subscription;

  get quickConfigvalue(): boolean { return this.quickConfigvalueSignal(); }
  set quickConfigvalue(value: boolean) { this.quickConfigvalueSignal.set(!!value); }

  get isIndoor(): boolean { return this.isIndoorSignal(); }
  set isIndoor(value: boolean) { this.isIndoorSignal.set(!!value); }

  get isFromDevice(): boolean { return this.isFromDeviceSignal(); }
  set isFromDevice(value: boolean) { this.isFromDeviceSignal.set(!!value); }

  constructor(
    private ref: ChangeDetectorRef, private appState: AppStateService,
    private aloAoc: AloAocStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private misc: MiscFeatureStateService
  ) {
    super();
  }

  ngOnInit() {
    this.quickConfigSub = this.aloAoc.getquickConfig.subscribe((data) => {
      if (data === "quickConfig") {
        this.quickConfigvalue = true;
      } else if (data === "standard") {
        this.quickConfigvalue = false;
      } else {
        // If data does not match above two values, assign quickConfigvalue as modes
        const modevalue = this.appState.getStoreConfigMode()?.storeConfigModeData;
        if (modevalue === "quickConfig") {
          this.quickConfigvalue = true;
        } else if (modevalue === "standard") {
          this.quickConfigvalue = false;
        }
      }
      // detectChanges is deferred to after buildForm() below so the template
      // receives the populated flextuneFormGroup on the very first render.
    });

    if (this.field) {
      this.properties = this.field.properties;
      this.applicableProperties = Array.isArray(this.field.requiredProperties)
        ? [...this.field.requiredProperties]
        : [];
      if (this.applicableProperties.length == 13) {
        this.isIndoor = true;
      }
      if (!this.applicableProperties.includes("WarmAOC")) {
        this.applicableProperties.push("WarmAOC");
      }
      this.applicableProperties
        .concat(["warmLightRatedLumen", "coolLightRatedLumen"])
        .forEach((prop) => {
          this.flextuneFormGroup.addControl(
            prop,
            new FormControl({
              value: null,
              disabled: ["warmLightRatedLumen", "coolLightRatedLumen"].includes(
                prop
              ),
            })
          );
        });
      this.allDropDownItems["LightOutputOverCct"] = [
        ...this.properties.LightOutputOverCct.enum,
      ];

      this.buildForm();

      this.formValueChangesSub = this.flextuneFormGroup.valueChanges.subscribe(() => {
        this.saveValueOnChange();
        this.ref.markForCheck();
      });

      this.restoreDefaultSub = this.featureDefaults.restoreFlextuneDefault.subscribe(() => {
        this.restoreDefault();
        this.ref.markForCheck();
      });
    }

    // Run initial CD now that both quickConfigvalue signal AND flextuneFormGroup are ready.
    this.ref.detectChanges();
  }

  ngOnDestroy() {
    if (this.quickConfigSub) {
      this.quickConfigSub.unsubscribe();
    }
    if (this.formValueChangesSub) {
      this.formValueChangesSub.unsubscribe();
    }
    if (this.restoreDefaultSub) {
      this.restoreDefaultSub.unsubscribe();
    }
    Object.keys(this.valueChangesSubs).forEach((key) => {
      if (this.valueChangesSubs[key]) {
        this.valueChangesSubs[key].unsubscribe();
      }
    });
    this.valueChangesSubs = {};
  }

  saveValueOnChange() {
    const warmAocControl = this.flextuneFormGroup.get("WarmAOC");
    const disabled = warmAocControl.disabled;
    if (disabled) warmAocControl.enable({ emitEvent: false });
    this.setValueInLocal();
    this.featureDefaults.setFlextuneDefaultColor(this.checkDefault());
    this.sendFlextuneData();
    // Re-disable WarmAOC BEFORE checking validity so that disabled controls
    // (which Angular excludes from form validation) don't cause a spurious
    // invalid state when WarmAOC has no value assigned.
    if (disabled) warmAocControl.disable({ emitEvent: false });
    this.misc.sendFlextuneValidity(this.flextuneFormGroup.valid);
  }

  preparePropValues(propName, initialValue) {
    const propData = this.properties[propName];
    if (!propData) return;
    if (!this.propertiesInfo[propName]) {
      this.propertiesInfo[propName] = {};
    }
    const info = this.propertiesInfo[propName];
    info["default"] = propData.default;
    info["value"] = initialValue;
  }

  preparePropertyConstraints(propName, options = {}) {
    const propData = this.properties[propName];
    if (!propData) return;
    if (!this.propertiesInfo[propName]) {
      this.propertiesInfo[propName] = {};
    }
    const info = this.propertiesInfo[propName];
    if (options["hasValueFromLocal"]) {
      for (const key in options) {
        if (key != "hasValueFromLocal" && key != "value") {
          info[key] = options[key];
        }
      }
      return;
    }
    let min = propData.minimum;
    let max = propData.maximum;
    let sliderMin = min;
    let sliderMax = max;
    if (propName == "WarmCct") {
      max = this.properties["CoolCct"].maximum;
      sliderMax = max;
    }
    if (propName == "CoolCct") {
      min = this.properties["WarmCct"].minimum;
      sliderMin = min;
    }

    if (
      propName == "CctCoolest" ||
      propName == "PowerOnCct" ||
      propName == "SystemFailureCct" ||
      propName == "CctWarmest"
    ) {
      const CoolCct = this.flextuneFormGroup.get("CoolCct");
      const WarmCct = this.flextuneFormGroup.get("WarmCct");
      const CctWarmest = this.flextuneFormGroup.get("CctWarmest");
      const CctCoolest = this.flextuneFormGroup.get("CctCoolest");
      sliderMin = WarmCct.valid
        ? this.propertiesInfo["WarmCct"].value
        : info.sliderMin;
      sliderMax = CoolCct.valid
        ? this.propertiesInfo["CoolCct"].value
        : info.sliderMax;

      if (propName == "PowerOnCct" || propName == "SystemFailureCct") {
        min = CctWarmest.valid
          ? this.propertiesInfo["CctWarmest"].value
          : info.min;
        max = CctCoolest.valid
          ? this.propertiesInfo["CctCoolest"].value
          : info.max;
      }
      if (propName == "CctCoolest") {
        max = sliderMax;
        min = CctWarmest.valid
          ? this.propertiesInfo["CctWarmest"].value
          : info.min;
      }
      if (propName == "CctWarmest") {
        max = CctCoolest.valid
          ? this.propertiesInfo["CctCoolest"].value
          : info.max;
        min = sliderMin;
      }
    }

    info["min"] = min;
    info["max"] = max;
    info["sliderMax"] = sliderMax;
    info["sliderMin"] = sliderMin;
    info["options"] = {
      floor: sliderMin,
      ceil: sliderMax,
      minLimit: min,
      maxLimit: max,
    };
  }

  addValidatorForProp(propName) {
    if (!this.propertiesInfo[propName]) return;
    const control = this.flextuneFormGroup.controls[propName];
    const max = this.propertiesInfo[propName].max;
    const min = this.propertiesInfo[propName].min;
    let validators = [
      customMinMaxValidator(this.propertiesInfo, propName),
      Validators.required,
    ];
    if (propName == "CoolCct" || propName == "WarmCct") {
      const checkProp = propName == "CoolCct" ? "WarmCct" : "CoolCct";
      const condition =
        propName == "CoolCct" ? "greaterthanequal" : "lessthanequal";
      validators = [
        Validators.required,
        customCCTValidator(
          this.flextuneFormGroup,
          checkProp,
          condition,
          min,
          max,
          this.propertiesInfo[checkProp].min,
          this.propertiesInfo[checkProp].max
        ),
      ];
    }
    control.setValidators(validators);
  }

  syncFormControlWithPropInfoValue(propName) {
    const control = this.flextuneFormGroup.controls[propName];
    if (this.valueChangesSubs[propName]) return;
    this.valueChangesSubs[propName] = control.valueChanges.subscribe(
      (value) => {
        this.propertiesInfo[propName].value = value;
        if (propName == "CoolCct" || propName == "WarmCct") {
          const checkProp = propName == "CoolCct" ? "WarmCct" : "CoolCct";
          const checkControl = this.flextuneFormGroup.get(checkProp);
          this.updateOnlyValidity(checkProp);
          if (control.valid && checkControl.valid && this.isIndoor) {
            this.onCCTValueChange();
          }
        }
        if (control.valid) {
          if (propName === "CctCoolest" || propName === "CctWarmest") {
            this.onCctWarmestOrCoolestChange(propName);
          }
          if (
            [
              "CoolRatedLm",
              "WarmRatedLm",
              "CoolRatedCurrent",
              "WarmRatedCurrent",
            ].includes(propName)
          ) {
            this.updateConstraintsAndLmForAOC();
          }

          if (propName == "CoolAOC" || propName == "WarmAOC") {
            this.updateConstraintsAndLmForAOC(true);
          }
        }
        if (propName === "LightOutputOverCct") {
          this.onLightOutputChange();
        }
      }
    );
  }

  updateConstraintsAndLmForAOC(updateOnlyLumen = false) {
    const isConstant =
      this.propertiesInfo["LightOutputOverCct"].value != "Flexible";
    const coolratedLm =
      this.propertiesInfo["CoolRatedLm"].value > 0 || !isConstant
        ? this.propertiesInfo["CoolRatedLm"].value
        : 1;
    const coolratedCurrent =
      this.propertiesInfo["CoolRatedCurrent"].value > 0 || !isConstant
        ? this.propertiesInfo["CoolRatedCurrent"].value
        : 1;
    const warmratedLm =
      this.propertiesInfo["WarmRatedLm"].value > 0 || !isConstant
        ? this.propertiesInfo["WarmRatedLm"].value
        : 1;
    const warmratedCurrent =
      this.propertiesInfo["WarmRatedCurrent"].value > 0 || !isConstant
        ? this.propertiesInfo["WarmRatedCurrent"].value
        : 1;

    const getMax = (type, driverMax) => {
      return type == "CoolAOC"
        ? Math.min(
            Math.round(
            (driverMax * warmratedLm * coolratedCurrent) /
            (warmratedCurrent * coolratedLm)
          ),
          driverMax
        )
        : Math.min(
            Math.round(
            (driverMax * coolratedLm * warmratedCurrent) /
            (coolratedCurrent * warmratedLm)
          ),
          driverMax
        );
    };

    const getMin = (type, driverMin) => {
      return type == "CoolAOC"
        ? Math.max(
          Math.round(
            (driverMin * warmratedLm * coolratedCurrent) /
            (warmratedCurrent * coolratedLm)
          ),
          driverMin
        )
        : Math.max(
          Math.round(
            (driverMin * coolratedLm * warmratedCurrent) /
            (coolratedCurrent * warmratedLm)
          ),
          driverMin
        );
    };

    const updatePropInfo = (propName) => {
      const propData = this.propertiesInfo[propName];
      const schemaData = this.properties[propName];
      let min = isConstant
        ? getMin(propName, this.properties[propName].minimum)
        : this.properties[propName].minimum;
      let max = isConstant
        ? getMax(propName, this.properties[propName].maximum)
        : this.properties[propName].maximum;
      if (min < schemaData.minimum) min = schemaData.minimum;
      if (min > schemaData.maximum) min = schemaData.maximum;
      if (max < min) max = min;
      if (max > schemaData.maximum) max = propData.maximum;
      propData["min"] = min;
      propData["max"] = max;
      propData["options"] = {
        floor: propData["sliderMin"],
        ceil: propData["sliderMax"],
        minLimit: min,
        maxLimit: max,
      };
    };
    const updateAOCFormValue = (propName) => {
      const propData = this.propertiesInfo[propName];
      const value = this.flextuneFormGroup.get(propName).value;
      if (!isConstant || propName == "CoolAOC") {
        propData.value = value;
        // Skip clamping when loading from device so out-of-range values show red box
        if (!this.isFromDevice) {
          if (value < propData.min) propData.value = propData.min;
          if (value > propData.max) propData.value = propData.max;
        }
      } else {
        const ratedLm = this.flextuneFormGroup.get("warmLightRatedLumen").value;
        propData.value = Math.round((ratedLm * warmratedCurrent) / warmratedLm);
      }
      const obj = {};
      obj[propName] = propData.value;
      this.flextuneFormGroup.patchValue({ ...obj }, { emitEvent: false });
      this.updateOnlyValidity(propName);
    };

    const updateratedLm = (propName, ratedlmProp) => {
      const propData = this.propertiesInfo[propName];
      let lm = 0;
      if (isConstant) {
        lm = Math.round(
          (this.propertiesInfo["CoolAOC"].value * coolratedLm) /
          coolratedCurrent
        );
      } else {
        if (propName == "CoolAOC") {
          lm =
            coolratedLm > 0 && coolratedCurrent > 0
              ? Math.round((propData.value * coolratedLm) / coolratedCurrent)
              : 0;
        } else {
          lm =
            warmratedCurrent > 0 && warmratedLm > 0
              ? Math.round((propData.value * warmratedLm) / warmratedCurrent)
              : 0;
        }
        if (lm > 10000) lm = 10000;
      }
      const obj = {};
      obj[ratedlmProp] = lm;
      this.flextuneFormGroup.patchValue({ ...obj }, { emitEvent: false });
    };

    if (!updateOnlyLumen) {
      updatePropInfo("CoolAOC");
      updatePropInfo("WarmAOC");
      updateAOCFormValue("CoolAOC");
    }
    updateratedLm("CoolAOC", "coolLightRatedLumen");
    if (!isConstant && !updateOnlyLumen) {
      updateAOCFormValue("WarmAOC");
    }
    updateratedLm("WarmAOC", "warmLightRatedLumen");
    if (isConstant) {
      updateAOCFormValue("WarmAOC");
    }
    this.saveValueOnChange();
  }

  onLightOutputChange(checkOnlyDisable = false) {
    const value = this.propertiesInfo["LightOutputOverCct"].value;
    const warmAOCControl = this.flextuneFormGroup.get("WarmAOC");
    if (value == "Flexible") {
      warmAOCControl.enable({ emitEvent: false });
    } else {
      warmAOCControl.disable({ emitEvent: false });
    }
    if (!checkOnlyDisable) this.updateConstraintsAndLmForAOC();
  }

  onCCTValueChange() {
    let cctCoolestValue = this.flextuneFormGroup.get("CctCoolest").value;
    let cctWarmestValue = this.flextuneFormGroup.get("CctWarmest").value;
    const coolCctValue = this.flextuneFormGroup.get("CoolCct").value;
    const warmCctValue = this.flextuneFormGroup.get("WarmCct").value;

    if (cctCoolestValue != null) {
      cctCoolestValue = this.clampCctValue(cctCoolestValue, warmCctValue, coolCctValue, "WarmCct", "CoolCct");
      this.preparePropValues("CctCoolest", cctCoolestValue);
    }
    if (cctWarmestValue != null) {
      cctWarmestValue = this.clampCctValue(cctWarmestValue, warmCctValue, coolCctValue, "WarmCct", "CoolCct");
      this.preparePropValues("CctWarmest", cctWarmestValue);
    }
    this.preparePropertyConstraints("CctCoolest");
    this.preparePropertyConstraints("CctWarmest");
    this.flextuneFormGroup.patchValue(
      {
        CctCoolest: cctCoolestValue,
        CctWarmest: cctWarmestValue,
      },
      { emitEvent: false }
    );
    this.updateOnlyValidity("CctCoolest");
    this.updateOnlyValidity("CctWarmest");
    this.onCctWarmestOrCoolestChange("");
  }

  private clampCctValue(value: number, warmCctValue: number, coolCctValue: number, warmCtrlName: string, coolCtrlName: string): number {
    if (this.isFromDevice) {
      return value;
    }
    if (value > coolCctValue && this.flextuneFormGroup.get(coolCtrlName).valid) {
      value = coolCctValue;
    }
    if (value < warmCctValue && this.flextuneFormGroup.get(warmCtrlName).valid) {
      value = warmCctValue;
    }
    return value;
  }

  onCctWarmestOrCoolestChange(propName) {
    if (propName == "CctCoolest") {
      this.preparePropertyConstraints("CctWarmest");
    } else if (propName == "CctWarmest") {
      this.preparePropertyConstraints("CctCoolest");
    }
    const cctCoolest = this.flextuneFormGroup.get("CctCoolest");
    const cctWarmest = this.flextuneFormGroup.get("CctWarmest");
    let powerValue = this.flextuneFormGroup.get("PowerOnCct").value;
    let systemFailureValue =
      this.flextuneFormGroup.get("SystemFailureCct").value;

    if (!this.isFromDevice) {
      powerValue = this.clampPowerOrFailureValue(powerValue, cctWarmest, cctCoolest);
      systemFailureValue = this.clampPowerOrFailureValue(systemFailureValue, cctWarmest, cctCoolest);
    }

    this.preparePropValues("PowerOnCct", powerValue);
    this.preparePropValues("SystemFailureCct", systemFailureValue);
    this.preparePropertyConstraints("PowerOnCct");
    this.preparePropertyConstraints("SystemFailureCct");
    this.flextuneFormGroup.patchValue({
      PowerOnCct: powerValue,
      SystemFailureCct: systemFailureValue,
    });
  }

  private clampPowerOrFailureValue(value: number, cctWarmest, cctCoolest): number {
    if (value == null) {
      return value;
    }
    if (cctWarmest.valid && value < cctWarmest.value) {
      value = cctWarmest.value;
    }
    if (cctCoolest.valid && value > cctCoolest.value) {
      value = cctCoolest.value;
    }
    return value;
  }
  async onSliderValueChange(value, property) {
    const formvalue = {};
    formvalue[property] = value;
    this.flextuneFormGroup.patchValue({
      ...formvalue,
    });
  }

  setValueInLocal() {
    const value = {};
    for (const key in this.flextuneFormGroup.controls) {
      const data = this.propertiesInfo[key]
        ? { ...this.propertiesInfo[key] }
        : {};
      data["value"] = this.flextuneFormGroup.get(key).value;
      value[key] = data;
    }
    // Use centralized method to save feature data
    // This automatically saves to both ConfigurationData and SaveSetData
    this.appState.setFeatureData('flextuneObj', value);
  }

  getValueFromLocal() {
    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    const flextuneData = this.appState.getFeatureData('flextuneObj') || this.appState.getFeatureData('FlexTune');
    if (flextuneData) {
      // Mark data as coming from device if it's from FlexTune (not flextuneObj)
      if (!this.appState.getFeatureData('flextuneObj')) {
        flextuneData["fromDevice"] = true;
      }
      return flextuneData;
    }
    return null;
  }

  buildForm() {
    const localData = this.getValueFromLocal();
    let formData = {};
    const propConstraints = {};
    if (!localData) {
      this.applicableProperties.forEach((prop) => {
        const propData = this.properties[prop];
        formData[prop] = propData.default;
      });
    } else {
      if (localData["fromDevice"]) {
        formData = localData;
      } else {
        for (const key in this.flextuneFormGroup.controls) {
          formData[key] = localData[key].value;
          const constraints = localData[key];
          constraints["hasValueFromLocal"] = true;
          propConstraints[key] = constraints;
        }
      }
    }
    this.applicableProperties.forEach((prop) => {
      this.preparePropValues(prop, formData[prop]);
      this.syncFormControlWithPropInfoValue(prop);
    });
    this.applicableProperties.forEach((prop) => {
      this.preparePropertyConstraints(
        prop,
        propConstraints[prop] ? propConstraints[prop] : {}
      );
    });
    this.applicableProperties.forEach((prop) => {
      this.addValidatorForProp(prop);
    });
    this.flextuneFormGroup.patchValue({ ...formData }, { emitEvent: false });
    this.applicableProperties.forEach((prop) => {
      this.updateOnlyValidity(prop);
    });
    if (!localData || localData["fromDevice"]) {
      // Set flag so clamping is skipped for device data, preserving out-of-range values
      this.isFromDevice = !!localData && !!localData["fromDevice"];
      this.onLightOutputChange();
      if (this.isIndoor) this.onCCTValueChange();
      this.isFromDevice = false;
    } else {
      this.onLightOutputChange(true);
    }
    this.saveValueOnChange();
  }

  restoreDefault() {
    const data = {};
    this.applicableProperties.forEach((prop) => {
      const propData = this.propertiesInfo[prop];
      if (propData) {
        data[prop] = propData.default;
        propData["value"] = propData.default;
      }
    });
    this.flextuneFormGroup.patchValue(
      {
        ...data,
      },
      { emitEvent: false }
    );
    this.applicableProperties.forEach((prop) => {
      this.updateOnlyValidity(prop);
    });
    this.onLightOutputChange();
    if (this.isIndoor) this.onCCTValueChange();
    this.saveValueOnChange();
    this.ref.markForCheck();
  }

  updateOnlyValidity(controlName) {
    const control = this.flextuneFormGroup.get(controlName);
    const isdisabled = control.disabled;
    if (isdisabled) control.enable({ emitEvent: false });
    if (this.valueChangesSubs[controlName]) {
      this.valueChangesSubs[controlName].unsubscribe();
      delete this.valueChangesSubs[controlName];
      control.updateValueAndValidity();
      this.syncFormControlWithPropInfoValue(controlName);
    } else {
      control.updateValueAndValidity();
    }
    if (isdisabled) control.disable({ emitEvent: false });
  }

  checkDefault() {
    return !this.applicableProperties.some((prop) => {
      const propData = this.propertiesInfo[prop];
      const control = this.flextuneFormGroup.controls[prop];
      return control.value != propData.default;
    });
  }

  sendFlextuneData() {
    const data = {};
    this.applicableProperties.forEach((prop) => {
      const propData = this.propertiesInfo[prop];
      if (propData) data[prop] = propData.value;
    });
    this.misc.sendFlextuneData(data);
  }

  isControlEnabled(controlName) {
    return !!this.flextuneFormGroup.get(controlName)?.enabled;
  }
}
